/**
 * Formats text with proper capitalization and handles special cases
 * @param {string} text - The text to format
 * @returns {string} - The formatted text
 */
export const formatText = (text) => {
  if (!text || typeof text !== "string") {
    return text;
  }

  // Handle special cases
  const specialCases = {
    http: "HTTP",
    https: "HTTPS",
    url: "URL",
    api: "API",
    ui: "UI",
    ux: "UX",
    id: "ID",
    ip: "IP",
    pdf: "PDF",
    csv: "CSV",
    json: "JSON",
    xml: "XML",
    html: "HTML",
    css: "CSS",
    js: "JS",
    ts: "TS",
    php: "PHP",
    sql: "SQL",
    aws: "AWS",
    gcp: "GCP",
    azure: "Azure",
    crm: "CRM",
    erp: "ERP",
    saas: "SaaS",
    paas: "PaaS",
    iaas: "IaaS",
    b2b: "B2B",
    b2c: "B2C",
    c2c: "C2C",
    p2p: "P2P",
    iot: "IoT",
    ai: "AI",
    ml: "ML",
    nlp: "NLP",
    vr: "VR",
    ar: "AR",
    mr: "MR",
    xr: "XR",
    "ui/ux": "UI/UX",
    "api/rest": "API/REST",
    "http/https": "HTTP/HTTPS",
  };

  // Convert to lowercase for comparison
  const lowerText = text.toLowerCase();

  // Check if the entire text matches a special case
  if (specialCases[lowerText]) {
    return specialCases[lowerText];
  }

  // Check if text contains special cases (like "http url" -> "HTTP URL")
  let formattedText = text;

  // Replace special cases within the text
  Object.entries(specialCases).forEach(([lowerCase, upperCase]) => {
    const regex = new RegExp(`\\b${lowerCase}\\b`, "gi");
    formattedText = formattedText.replace(regex, upperCase);
  });

  // Split into words to handle special cases and then rejoin
  const words = formattedText.split(" ");

  // A word already fully uppercase in the source data (CEO, QA, IT, HR, ...)
  // is treated as an intentional acronym and preserved as-is - the
  // specialCases table above can't realistically enumerate every
  // business-role/department acronym orgs type into free-text fields like
  // Designation/Job role/Department, so an unlisted one used to get
  // silently lowercased (e.g. stored "CEO" rendered as "Ceo"), diverging
  // from what was actually typed/stored.
  const isShoutedWord = (word) => word.length > 1 && word === word.toUpperCase() && word !== word.toLowerCase();

  // Apply special case formatting to individual words
  const processedWords = words.map((word) => {
    const lowerWord = word.toLowerCase();
    if (specialCases[lowerWord]) {
      return specialCases[lowerWord];
    }
    if (isShoutedWord(word)) {
      return word;
    }
    // Anything else keeps whatever casing it already has - this is also
    // reused for genuine free-text fields (contact/company/billing-unit
    // names, etc.), not just lowercase enum values, so forcing every other
    // word to lowercase silently destroyed real Title Case input the user
    // typed (e.g. stored "CRUD Test Unit" rendered as "CRUD test unit").
    return word;
  });

  // Only the very first character is forced uppercase here - every word's
  // casing was already decided above (dictionary acronym, shouted-word
  // acronym, or plain lowercase), so it must not be blanket-lowercased
  // again (that used to silently undo any acronym beyond the very first
  // character of the whole string, e.g. "HTTP URL" -> "Http url").
  const joinedText = processedWords.join(" ");
  return joinedText.charAt(0).toUpperCase() + joinedText.slice(1);
};

/**
 * Formats a single word with proper capitalization
 * @param {string} word - The word to format
 * @returns {string} - The formatted word
 */
export const formatWord = (word) => {
  if (!word || typeof word !== "string") {
    return word;
  }

  const specialCases = {
    http: "HTTP",
    https: "HTTPS",
    url: "URL",
    api: "API",
    ui: "UI",
    ux: "UX",
    id: "ID",
    ip: "IP",
    pdf: "PDF",
    csv: "CSV",
    json: "JSON",
    xml: "XML",
    html: "HTML",
    css: "CSS",
    js: "JS",
    ts: "TS",
    php: "PHP",
    sql: "SQL",
    aws: "AWS",
    gcp: "GCP",
    azure: "Azure",
    crm: "CRM",
    erp: "ERP",
    saas: "SaaS",
    paas: "PaaS",
    iaas: "IaaS",
    b2b: "B2B",
    b2c: "B2C",
    c2c: "C2C",
    p2p: "P2P",
    iot: "IoT",
    ai: "AI",
    ml: "ML",
    nlp: "NLP",
    vr: "VR",
    ar: "AR",
    mr: "MR",
    xr: "XR",
  };

  const lowerWord = word.toLowerCase();

  if (specialCases[lowerWord]) {
    return specialCases[lowerWord];
  }

  // Same shouted-word acronym preservation as formatText above.
  if (word.length > 1 && word === word.toUpperCase() && word !== word.toLowerCase()) {
    return word;
  }

  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};
